/**
 * EPIC 47 P2a — FCM web-push client.
 *
 * Opt-in is EXPLICIT: nothing here runs on app load. The user presses "Enable
 * notifications" (NotificationBell) which calls `enablePush()` — that requests
 * the browser Notification permission, gets an FCM token, and registers it via
 * the `registerPushToken` callable (stored on `users/{uid}.pushTokens[]`).
 *
 * Config: the Web Push VAPID key is PUBLIC (Firebase console → Cloud Messaging →
 * Web configuration) and wired via `VITE_FIREBASE_VAPID_KEY`, mirroring the
 * PUBLIC_BASE_URL pattern. If it (or the messaging sender id) is unset — e.g. on
 * a dev box without the key yet — `isPushConfigured()` returns false and the UI
 * shows a "not configured" state instead of crashing. Push simply won't fire
 * until the key is set; everything else degrades gracefully.
 */

import { getToken, isSupported, onMessage, type MessagePayload } from 'firebase/messaging';

import { doc, onSnapshot } from 'firebase/firestore';

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase, getMessagingSafe } from './firebase';

export type PushState =
  | 'unsupported' // browser has no SW / Notification / FCM support
  | 'not-configured' // VAPID key / sender id missing
  | 'default' // permission not yet asked
  | 'denied' // user blocked notifications
  | 'enabled'; // permission granted + token registered

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

const SW_URL = '/firebase-messaging-sw.js';

/**
 * PWA-2 — the FCM worker MUST NOT own the root scope any more.
 *
 * Only one service worker can control a given scope, and registering a second
 * script at `/` REPLACES the first. Since PWA-2 the app-shell worker (`sw.js`,
 * generated by vite-plugin-pwa) owns `/` so it can serve navigations offline;
 * leaving FCM at `/` would make the two evict each other on alternating loads.
 *
 * A narrow scope costs nothing: push events are delivered to the registration
 * that created the subscription regardless of scope, and notification clicks go
 * through `clients.openWindow`, which is scope-independent. This is the same path
 * the Firebase SDK picks by default when no registration is passed.
 */
const SW_SCOPE = '/firebase-cloud-messaging-push-scope';

/** True only when the public VAPID key + messaging sender id are configured. */
export function isPushConfigured(): boolean {
  return Boolean(VAPID_KEY && import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID);
}

function browserSupportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'Notification' in window &&
    'PushManager' in window
  );
}

/** Synchronous best-effort snapshot of the current state (no permission prompt). */
export function currentPushState(): PushState {
  if (!browserSupportsPush()) return 'unsupported';
  if (!isPushConfigured()) return 'not-configured';
  const perm = Notification.permission;
  if (perm === 'granted') return 'enabled';
  if (perm === 'denied') return 'denied';
  return 'default';
}

/**
 * Register our own SW instance carrying the (public) Firebase config in the query
 * string, so `firebase-messaging-sw.js` can init without hardcoding a project.
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const p = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
  });
  return navigator.serviceWorker.register(`${SW_URL}?${p.toString()}`, { scope: SW_SCOPE });
}

/**
 * Explicit opt-in. Requests permission (if not already decided), obtains the FCM
 * token, and registers it. Returns the resulting state so the caller can show the
 * right message. Never throws for the "expected" outcomes (unsupported / denied /
 * not-configured); genuine unexpected errors still reject so the error logger
 * captures them.
 */
export async function enablePush(): Promise<PushState> {
  if (!browserSupportsPush()) return 'unsupported';
  if (!isPushConfigured()) return 'not-configured';
  if (!(await isSupported().catch(() => false))) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return permission === 'denied' ? 'denied' : 'default';
  }

  const messaging = getMessagingSafe();
  if (!messaging) return 'unsupported';

  const registration = await registerServiceWorker();
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) return 'default';

  const { functions } = getFirebase();
  const callable = httpsCallable<{ token: string }, { ok: true }>(functions, 'registerPushToken');
  await callable({ token });
  return 'enabled';
}

/**
 * Best-effort opt-out / cleanup: unregister the current device token (called on
 * explicit disable and on logout). Never throws — a failure here must not block
 * sign-out.
 */
export async function disablePush(): Promise<void> {
  try {
    if (!browserSupportsPush() || !isPushConfigured()) return;
    if (Notification.permission !== 'granted') return;
    const messaging = getMessagingSafe();
    if (!messaging) return;
    // Look up by SCOPE, not script URL: `getRegistration` resolves a *document*
    // URL against registered scopes, so passing `SW_URL` would now match the
    // root-scoped app-shell worker instead of the FCM one.
    const registration = await navigator.serviceWorker
      .getRegistration(SW_SCOPE)
      .catch(() => undefined);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration ?? undefined,
    }).catch(() => undefined);
    if (!token) return;
    const { functions } = getFirebase();
    const callable = httpsCallable<{ token: string }, { ok: true }>(functions, 'unregisterPushToken');
    await callable({ token });
  } catch {
    /* swallow — opt-out is best-effort */
  }
}

/**
 * Subscribe to foreground messages (app tab focused) so a push that arrives while
 * the app is open isn't silently dropped. Returns an unsubscribe fn, or a no-op if
 * push isn't configured/supported.
 */
export function onForegroundPush(cb: (payload: MessagePayload) => void): () => void {
  if (!browserSupportsPush() || !isPushConfigured()) return () => {};
  const messaging = getMessagingSafe();
  if (!messaging) return () => {};
  try {
    return onMessage(messaging, cb);
  } catch {
    return () => {};
  }
}

/**
 * The one switch for OPTIONAL pushes — `users/{uid}.settings.notificationsEnabled`
 * (absent = on). Mandatory pushes ignore it (Marti 2026-10-22).
 */
export function subscribeOptionalPushEnabled(
  uid: string,
  onValue: (enabled: boolean) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    doc(firestore, 'users', uid),
    (snap) => onValue(snap.data()?.settings?.notificationsEnabled !== false),
    () => onValue(true),
  );
}

export async function setOptionalPushEnabled(enabled: boolean): Promise<void> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ enabled: boolean }, { ok: true; enabled: boolean }>(
    functions,
    'setOptionalPushEnabled',
  );
  await callable({ enabled });
}
